import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../lib/api';
import useIsMobile from '../../lib/useIsMobile';
import MarketingNav from './MarketingNav';
import Footer from './Footer';
import { ClearSegFull, ClearSegIcon } from '../ui/ClearSegLogo';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  green: '#16A34A',
  muted: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
};

const fmt = (n) => Number(n).toLocaleString('en-US');

export default function AffiliateLanding() {
  const { code } = useParams();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [fee, setFee] = useState('799');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [pageRes, feeRes] = await Promise.all([
          api.get(`/affiliates/page/${encodeURIComponent(code)}`),
          api.get('/config/fees'),
        ]);
        setData(pageRes.data);
        const val = feeRes.data?.single_family;
        if (val) setFee(fmt(val));
        if (pageRes.data.logoR2Key) {
          try {
            const logoRes = await api.get(`/affiliates/logo/${encodeURIComponent(code)}`);
            setLogoUrl(logoRes.data.url);
          } catch { /* no logo */ }
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: C.muted, fontSize: 15 }}>Loading...</div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg }}>
        <MarketingNav />
        <div style={{ textAlign: 'center', padding: '120px 20px' }}>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: C.navy, marginBottom: 12 }}>Partner Not Found</h1>
          <p style={{ fontSize: 16, color: C.muted, marginBottom: 32 }}>
            This referral link is not active. Please check the URL and try again.
          </p>
          <Link to="/" style={{
            display: 'inline-block', padding: '14px 32px', borderRadius: 10,
            background: C.navy, color: '#FFFFFF', fontSize: 15, fontWeight: 700,
            textDecoration: 'none',
          }}>
            Visit ClearSeg
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const headline = data.customHeadline || 'Save Tens of Thousands on Taxes for Your Rental Property';
  const description = data.customDescription ||
    `${data.name} at ${data.firm || 'their firm'} has partnered with ClearSeg to help you accelerate your depreciation deductions.`;
  const ctaUrl = `/intake?ref=${data.referralCode}`;

  const cards = [
    { num: 1, title: 'Identify', desc: 'We analyze every component of your rental property \u2014 electrical, plumbing, flooring, cabinetry, fixtures, landscaping, and more.' },
    { num: 2, title: 'Reclassify', desc: 'Components are reclassified from 27.5-year residential depreciation to 5, 7, or 15-year recovery periods per IRS guidelines.' },
    { num: 3, title: 'Save', desc: 'You claim accelerated depreciation deductions on your next tax return \u2014 the average ClearSeg client saves $37,400 in year-one tax savings.' },
  ];

  const steps = [
    { num: '1', title: 'Submit Property Info', desc: 'Fill out a simple intake form with your property details.' },
    { num: '2', title: 'We Analyze Everything', desc: 'Our engineers identify every depreciable component of your property.' },
    { num: '3', title: 'PE-Certified Report', desc: 'You receive a professional engineer-certified cost segregation study.' },
    { num: '4', title: 'File & Save', desc: 'Your CPA files the study with your tax return. You keep more money.' },
  ];

  const trustItems = [
    'PE-Certified',
    'IRS-Compliant',
    `Recommended by ${data.name}`,
  ];

  const sectionPad = isMobile ? '60px 20px' : '80px 24px';
  const maxW = { maxWidth: 1100, margin: '0 auto' };

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <MarketingNav />

      {/* Partner Header */}
      <section style={{ padding: isMobile ? '40px 20px 0' : '50px 24px 0', textAlign: 'center' }}>
        <div style={maxW}>
          {logoUrl ? (
            <div>
              <img src={logoUrl} alt={`${data.firm || data.name} logo`}
                style={{ maxHeight: isMobile ? 60 : 80, maxWidth: isMobile ? 200 : 300, objectFit: 'contain', marginBottom: 16 }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>Powered by</span>
                <ClearSegIcon size={20} />
                <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>ClearSeg</span>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                display: 'inline-block', padding: '6px 18px', borderRadius: 20,
                background: `${C.accent}08`, border: `1px solid ${C.accent}22`,
                fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 16,
              }}>
                Recommended by {data.name}
              </div>
              {data.firm && <div style={{ fontSize: 14, color: C.muted, marginBottom: 8 }}>{data.firm}</div>}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>Powered by</span>
                <ClearSegIcon size={20} />
                <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>ClearSeg</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Hero */}
      <section style={{ padding: sectionPad, textAlign: 'center' }}>
        <div style={{ ...maxW, maxWidth: 800 }}>
          <h1 style={{
            fontSize: isMobile ? 28 : 52, fontWeight: 800, color: C.navy,
            margin: '0 0 20px', lineHeight: 1.12, letterSpacing: -1.5,
          }}>
            {headline}
          </h1>
          <p style={{
            fontSize: isMobile ? 15 : 19, color: C.muted, margin: '0 0 40px',
            lineHeight: 1.7, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto',
          }}>
            {description}
          </p>
          <Link to={ctaUrl} style={{
            display: 'inline-block', padding: isMobile ? '14px 32px' : '16px 44px',
            borderRadius: 10, background: C.navy, color: '#FFFFFF',
            fontSize: isMobile ? 15 : 17, fontWeight: 700, textDecoration: 'none',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            Start Your Study
          </Link>
        </div>
      </section>

      {/* 3 Cards */}
      <section style={{ padding: sectionPad }}>
        <div style={maxW}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 20 : 28,
          }}>
            {cards.map((c) => (
              <div key={c.num} style={{
                background: C.white, borderRadius: 12,
                border: `1px solid ${C.border}`,
                padding: isMobile ? '28px 22px' : '36px 28px',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: C.navy, color: '#FFFFFF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 800, marginBottom: 18,
                }}>
                  {c.num}
                </div>
                <h3 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: C.navy, margin: '0 0 10px' }}>
                  {c.title}
                </h3>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, margin: 0 }}>
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ padding: sectionPad, background: C.white }}>
        <div style={maxW}>
          <h2 style={{
            fontSize: isMobile ? 24 : 36, fontWeight: 800, color: C.navy,
            textAlign: 'center', margin: '0 0 16px',
          }}>
            How It Works
          </h2>
          <p style={{
            fontSize: isMobile ? 14 : 16, color: C.muted, textAlign: 'center',
            margin: '0 auto 48px', maxWidth: 500, lineHeight: 1.6,
          }}>
            Four simple steps to accelerated depreciation savings.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: isMobile ? 20 : 24,
          }}>
            {steps.map((st) => (
              <div key={st.num} style={{ textAlign: 'center' }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: '#F0FDF4', border: '1.5px solid #BBF7D0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: C.green, fontSize: 20, fontWeight: 800,
                  margin: '0 auto 16px',
                }}>
                  {st.num}
                </div>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: C.navy, margin: '0 0 8px' }}>
                  {st.title}
                </h4>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: 0 }}>
                  {st.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: sectionPad }}>
        <div style={{ ...maxW, textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 800, color: C.navy, margin: '0 0 16px' }}>
            Simple, Transparent Pricing
          </h2>
          <p style={{ fontSize: isMobile ? 14 : 16, color: C.muted, margin: '0 0 40px' }}>
            One flat fee. No hidden costs. No surprises.
          </p>
          <div style={{
            display: 'inline-block', background: C.white, borderRadius: 16,
            border: `2px solid ${C.navy}`, padding: isMobile ? '36px 28px' : '48px 56px',
            maxWidth: 420, width: '100%',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
              Single-Family Residential
            </div>
            <div style={{
              fontSize: isMobile ? 48 : 60, fontWeight: 800, color: C.navy,
              letterSpacing: -2, lineHeight: 1, marginBottom: 8,
            }}>
              ${fee}
            </div>
            <div style={{ fontSize: 15, color: C.muted, marginBottom: 32 }}>per property</div>
            <Link to={ctaUrl} style={{
              display: 'inline-block', padding: '14px 36px', borderRadius: 10,
              background: C.navy, color: '#FFFFFF', fontSize: 15, fontWeight: 700,
              textDecoration: 'none', transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              Get Started
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section style={{ padding: isMobile ? '40px 20px' : '50px 24px' }}>
        <div style={{
          ...maxW, display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'center', alignItems: 'center',
          gap: isMobile ? 16 : 48,
        }}>
          {trustItems.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7,
                background: '#F0FDF4', border: '1px solid #BBF7D0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: C.green, fontSize: 14, fontWeight: 700,
              }}>
                &#10003;
              </div>
              <span style={{ fontSize: isMobile ? 14 : 15, color: C.navy, fontWeight: 600 }}>{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ padding: sectionPad, background: C.navy, textAlign: 'center' }}>
        <div style={{ ...maxW, maxWidth: 700 }}>
          <h2 style={{
            fontSize: isMobile ? 24 : 40, fontWeight: 800, color: '#FFFFFF',
            margin: '0 0 16px',
          }}>
            Ready to Save on Your Rental Property?
          </h2>
          <p style={{
            fontSize: isMobile ? 14 : 17, color: '#94A3B8', margin: '0 0 36px', lineHeight: 1.6,
          }}>
            Join thousands of property owners who have used cost segregation to accelerate their depreciation deductions.
          </p>
          <Link to={ctaUrl} style={{
            display: 'inline-block', padding: isMobile ? '14px 32px' : '16px 44px',
            borderRadius: 10, background: '#FFFFFF', color: C.navy,
            fontSize: isMobile ? 15 : 17, fontWeight: 700, textDecoration: 'none',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.92'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            Start Your Study
          </Link>
        </div>
      </section>

      <div style={{
        textAlign: 'center', padding: '16px 20px 0',
        borderTop: `1px solid ${C.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: C.muted }}>Powered by</span>
          <ClearSegFull iconSize={20} textSize={13} color={C.muted} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
